import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { communityAPI } from '../services/api';
import type { CommunityProfile, User } from '../types';

const asOwnerEmail = (owner: any) => {
  if (!owner) return '';
  if (typeof owner === 'string') return '';
  return owner.email || '';
};

const servicesToString = (arr: any) => {
  if (!Array.isArray(arr)) return '';
  return arr.join(', ');
};

const stringToServices = (s: string) => {
  return s
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
};

type Editable = {
  profileType: 'company' | 'individual';
  companyName: string;
  displayName: string;
  logoDataUrl: string;
  summary: string;
  about: string;
  servicesText: string;
  country: string;
  city: string;
  contactEmail: string;
  websiteUrl: string;
  featured: boolean;
  status: 'draft' | 'published';
};

type CreateOwner = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
};

const emptyEditable = (): Editable => ({
  profileType: 'company',
  companyName: '',
  displayName: '',
  logoDataUrl: '',
  summary: '',
  about: '',
  servicesText: '',
  country: '',
  city: '',
  contactEmail: '',
  websiteUrl: '',
  featured: false,
  status: 'draft',
});

const CommunityAdmin: React.FC = () => {
  const [items, setItems] = useState<CommunityProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [openCreate, setOpenCreate] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);

  const [selected, setSelected] = useState<CommunityProfile | null>(null);
  const [editable, setEditable] = useState<Editable>(emptyEditable());
  const [owner, setOwner] = useState<CreateOwner>({ firstName: '', lastName: '', email: '', password: '' });

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await communityAPI.adminList();
      setItems(res.data || []);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const sorted = useMemo(() => {
    return [...items].sort((a: any, b: any) => {
      const af = a.featured ? 1 : 0;
      const bf = b.featured ? 1 : 0;
      if (af !== bf) return bf - af;
      const ad = new Date(a.updatedAt || a.createdAt || 0).getTime();
      const bd = new Date(b.updatedAt || b.createdAt || 0).getTime();
      return bd - ad;
    });
  }, [items]);

  const fillEditableFromProfile = (p: CommunityProfile) => {
    setEditable({
      profileType: p.profileType,
      companyName: p.companyName || '',
      displayName: p.displayName || '',
      logoDataUrl: p.logoDataUrl || '',
      summary: p.summary || '',
      about: p.about || '',
      servicesText: servicesToString(p.services),
      country: p.location?.country || '',
      city: p.location?.city || '',
      contactEmail: p.contactEmail || '',
      websiteUrl: p.websiteUrl || '',
      featured: Boolean(p.featured),
      status: p.status,
    });
  };

  const handleOpenCreate = () => {
    setSuccess(null);
    setError(null);
    setOwner({ firstName: '', lastName: '', email: '', password: '' });
    setEditable(emptyEditable());
    setOpenCreate(true);
  };

  const handleCreate = async () => {
    try {
      setError(null);
      setSuccess(null);
      await communityAPI.adminCreate({
        ...owner,
        profileType: editable.profileType,
        companyName: editable.companyName,
        displayName: editable.displayName,
        logoDataUrl: editable.logoDataUrl,
        summary: editable.summary,
        about: editable.about,
        services: stringToServices(editable.servicesText),
        location: { country: editable.country, city: editable.city },
        contactEmail: editable.contactEmail,
        websiteUrl: editable.websiteUrl,
        featured: editable.featured,
        status: editable.status,
      });
      setOpenCreate(false);
      setSuccess('Profile created');
      await refresh();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to create profile');
    }
  };

  const handleOpenEdit = (p: CommunityProfile) => {
    setSuccess(null);
    setError(null);
    setSelected(p);
    fillEditableFromProfile(p);
    setOpenEdit(true);
  };

  const handleSaveEdit = async () => {
    if (!selected?._id) return;
    try {
      setError(null);
      setSuccess(null);
      await communityAPI.adminUpdate(selected._id, {
        profileType: editable.profileType,
        companyName: editable.companyName,
        displayName: editable.displayName,
        logoDataUrl: editable.logoDataUrl,
        summary: editable.summary,
        about: editable.about,
        services: stringToServices(editable.servicesText),
        location: { country: editable.country, city: editable.city },
        contactEmail: editable.contactEmail,
        websiteUrl: editable.websiteUrl,
        featured: editable.featured,
        status: editable.status,
      });
      setOpenEdit(false);
      setSelected(null);
      setSuccess('Profile updated');
      await refresh();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update profile');
    }
  };

  const handleOpenDelete = (p: CommunityProfile) => {
    setSuccess(null);
    setError(null);
    setSelected(p);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    if (!selected?._id) return;
    try {
      setError(null);
      setSuccess(null);
      await communityAPI.adminDelete(selected._id);
      setOpenDelete(false);
      setSelected(null);
      setSuccess('Profile deleted');
      await refresh();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to delete profile');
    }
  };

  const quickPublish = async (p: CommunityProfile, status: 'draft' | 'published') => {
    try {
      setError(null);
      setSuccess(null);
      await communityAPI.adminUpdate(p._id, { status });
      setSuccess(status === 'published' ? 'Profile published' : 'Profile set to draft');
      await refresh();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update status');
    }
  };

  const quickFeatured = async (p: CommunityProfile, featured: boolean) => {
    try {
      setError(null);
      setSuccess(null);
      await communityAPI.adminUpdate(p._id, { featured });
      setSuccess(featured ? 'Marked as featured' : 'Unfeatured');
      await refresh();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Failed to update featured');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 900 }}>
          Community Directory
        </Typography>
        <Button variant="contained" onClick={handleOpenCreate}>
          Add profile
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Featured</TableCell>
              <TableCell>Owner email</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((p) => (
              <TableRow key={p._id} hover>
                <TableCell>{p.displayName}</TableCell>
                <TableCell>{p.profileType}</TableCell>
                <TableCell>{p.status}</TableCell>
                <TableCell>
                  <Checkbox
                    checked={Boolean(p.featured)}
                    onChange={(e) => quickFeatured(p, e.target.checked)}
                  />
                </TableCell>
                <TableCell>{asOwnerEmail(p.owner as User)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    {p.status === 'draft' ? (
                      <Button size="small" variant="outlined" onClick={() => quickPublish(p, 'published')}>
                        Publish
                      </Button>
                    ) : (
                      <Button size="small" variant="outlined" onClick={() => quickPublish(p, 'draft')}>
                        Unpublish
                      </Button>
                    )}
                    <Button size="small" variant="outlined" onClick={() => handleOpenEdit(p)}>
                      Edit
                    </Button>
                    <Button size="small" color="error" variant="outlined" onClick={() => handleOpenDelete(p)}>
                      Delete
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={openCreate} onClose={() => setOpenCreate(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add profile</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="subtitle2" color="text.secondary">Owner account</Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="First name" value={owner.firstName} onChange={(e) => setOwner({ ...owner, firstName: e.target.value })} fullWidth required />
              <TextField label="Last name" value={owner.lastName} onChange={(e) => setOwner({ ...owner, lastName: e.target.value })} fullWidth required />
            </Stack>
            <TextField label="Email" value={owner.email} onChange={(e) => setOwner({ ...owner, email: e.target.value })} fullWidth required />
            <TextField label="Password" type="password" value={owner.password} onChange={(e) => setOwner({ ...owner, password: e.target.value })} fullWidth required />

            <TextField
              select
              label="Profile type"
              value={editable.profileType}
              onChange={(e) => setEditable({ ...editable, profileType: e.target.value as any })}
              fullWidth
            >
              <MenuItem value="company">Company</MenuItem>
              <MenuItem value="individual">Individual</MenuItem>
            </TextField>

            <TextField label="Company name" value={editable.companyName} onChange={(e) => setEditable({ ...editable, companyName: e.target.value })} fullWidth />
            <TextField label="Display name" value={editable.displayName} onChange={(e) => setEditable({ ...editable, displayName: e.target.value })} fullWidth required />
            <TextField label="Logo (data URL)" value={editable.logoDataUrl} onChange={(e) => setEditable({ ...editable, logoDataUrl: e.target.value })} fullWidth />
            <TextField label="Summary" value={editable.summary} onChange={(e) => setEditable({ ...editable, summary: e.target.value })} fullWidth />
            <TextField label="About" value={editable.about} onChange={(e) => setEditable({ ...editable, about: e.target.value })} fullWidth multiline minRows={4} />
            <TextField label="Services (comma separated)" value={editable.servicesText} onChange={(e) => setEditable({ ...editable, servicesText: e.target.value })} fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Country" value={editable.country} onChange={(e) => setEditable({ ...editable, country: e.target.value })} fullWidth />
              <TextField label="City" value={editable.city} onChange={(e) => setEditable({ ...editable, city: e.target.value })} fullWidth />
            </Stack>
            <TextField label="Contact email" value={editable.contactEmail} onChange={(e) => setEditable({ ...editable, contactEmail: e.target.value })} fullWidth />
            <TextField label="Website URL" value={editable.websiteUrl} onChange={(e) => setEditable({ ...editable, websiteUrl: e.target.value })} fullWidth />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Status"
                value={editable.status}
                onChange={(e) => setEditable({ ...editable, status: e.target.value as any })}
                fullWidth
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </TextField>
              <FormControlLabel
                control={<Checkbox checked={editable.featured} onChange={(e) => setEditable({ ...editable, featured: e.target.checked })} />}
                label="Featured"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openEdit} onClose={() => setOpenEdit(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit profile</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Profile type"
              value={editable.profileType}
              onChange={(e) => setEditable({ ...editable, profileType: e.target.value as any })}
              fullWidth
            >
              <MenuItem value="company">Company</MenuItem>
              <MenuItem value="individual">Individual</MenuItem>
            </TextField>
            <TextField label="Company name" value={editable.companyName} onChange={(e) => setEditable({ ...editable, companyName: e.target.value })} fullWidth />
            <TextField label="Display name" value={editable.displayName} onChange={(e) => setEditable({ ...editable, displayName: e.target.value })} fullWidth required />
            <TextField label="Logo (data URL)" value={editable.logoDataUrl} onChange={(e) => setEditable({ ...editable, logoDataUrl: e.target.value })} fullWidth />
            <TextField label="Summary" value={editable.summary} onChange={(e) => setEditable({ ...editable, summary: e.target.value })} fullWidth />
            <TextField label="About" value={editable.about} onChange={(e) => setEditable({ ...editable, about: e.target.value })} fullWidth multiline minRows={4} />
            <TextField label="Services (comma separated)" value={editable.servicesText} onChange={(e) => setEditable({ ...editable, servicesText: e.target.value })} fullWidth />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Country" value={editable.country} onChange={(e) => setEditable({ ...editable, country: e.target.value })} fullWidth />
              <TextField label="City" value={editable.city} onChange={(e) => setEditable({ ...editable, city: e.target.value })} fullWidth />
            </Stack>
            <TextField label="Contact email" value={editable.contactEmail} onChange={(e) => setEditable({ ...editable, contactEmail: e.target.value })} fullWidth />
            <TextField label="Website URL" value={editable.websiteUrl} onChange={(e) => setEditable({ ...editable, websiteUrl: e.target.value })} fullWidth />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Status"
                value={editable.status}
                onChange={(e) => setEditable({ ...editable, status: e.target.value as any })}
                fullWidth
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </TextField>
              <FormControlLabel
                control={<Checkbox checked={editable.featured} onChange={(e) => setEditable({ ...editable, featured: e.target.checked })} />}
                label="Featured"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit}>Save</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDelete} onClose={() => setOpenDelete(false)}>
        <DialogTitle>Delete profile</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete {selected?.displayName}?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommunityAdmin;
